/**
 * The inference view: soft-category claims and single-hop relations, over one
 * artifact version (inference-claims task 6.2).
 *
 * This is the **least certain** output in the product, and the view is built to say
 * so at every turn rather than let a claim read like a fact once it is on screen:
 *
 * - **Coverage is never collapsible.** Examined-of-eligible sits at the top, always
 *   visible, the same "a thin result must carry its denominator" posture the dossier
 *   view already established.
 * - **A flood flag is a banner, not a footnote.** A run whose claims-per-window
 *   exceeds the pinned base-rate ceiling (design D7) says so on every claim it
 *   produced, in place, not buried in a stats panel nobody opens.
 * - **Every citation names its support method.** `llm_soft_category` on the claim
 *   itself, `span_recovery_exact`/`span_recovery_normalized` on each citation — a
 *   reader can always tell *how* a span was grounded, never just *that* it was.
 * - **Mention-only is the default, stated as such.** No client-side filter narrows
 *   what a completed run already produced — narrowing happens at generation time
 *   (design D6), so this view names the setting the run was made with instead of
 *   pretending to control it after the fact.
 */

import { useEffect, useState } from "react";
import { EvidencePanel } from "../components/EvidencePanel";
import {
  api,
  isInferenceLayout,
  type ArtifactVersion,
  type Unit,
  type UnitRef,
} from "../lib/api";
import { useEvidence } from "../state/evidence";

interface Props {
  datasetId: string;
  artifactId: string;
  versionId: string;
}

export function Inference({ datasetId, artifactId, versionId }: Props) {
  const [version, setVersion] = useState<ArtifactVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .version(datasetId, artifactId, versionId)
      .then((fetched) => {
        if (!cancelled) setVersion(fetched);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId, artifactId, versionId]);

  if (error) {
    return (
      <main className="page">
        <p data-testid="inference-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      </main>
    );
  }
  if (!version) return <main className="page">loading…</main>;

  if (!isInferenceLayout(version.layout)) {
    return (
      <main className="page">
        <p data-testid="inference-wrong-kind" style={{ color: "var(--danger)" }}>
          This artifact version is not an inference run.
        </p>
      </main>
    );
  }
  const layout = version.layout;
  const stats = version.stats ?? {};
  const flood = (stats.flood as Record<string, { rate: number; ceiling: number }>) ?? {};
  const floodCategories = Object.keys(flood);

  const rows = layout.units
    .map((ref) => ({ ref, unit: version.units[ref.u] }))
    .filter((row): row is { ref: UnitRef; unit: Unit } => !!row.unit);

  const claimsAndRelations = rows.filter(
    (r) => r.unit.kind === "claim" || (r.unit.kind === "extractive" && r.unit.origin === "model"),
  );
  const coverageRows = rows.filter((r) => r.unit.kind === "coverage");
  const notAskedRows = rows.filter((r) => r.unit.kind === "not_asked");
  const gapRows = rows.filter((r) => r.unit.kind === "gap");

  return (
    <div className="workspace two-pane">
      <section className="pane left">
        <main className="page inference" data-testid="inference-view">
          <header>
            <h1>{version.title}</h1>
            <p className="eyebrow" data-testid="inference-status">
              {layout.ask === "soft_claims" ? "Soft claims" : "Relations"} · v
              {version.version_no} · {version.status}
              {version.stale ? " · stale" : ""}
            </p>
            <p style={{ color: "var(--ink-faint)", maxWidth: "70ch" }}>
              {layout.ask === "soft_claims"
                ? "An inference — visibly one. Every claim below is the model's authored " +
                  "sentence, re-grounded server-side to exact characters; it is never a " +
                  "fact the corpus states outright."
                : "Single-hop only: each relation restates what one cited passage says. " +
                  "A relation requiring more than one passage is refused, not guessed."}
            </p>
            <p>
              <a
                href={api.exportUrl(datasetId, artifactId, versionId)}
                data-testid="export-link"
                download
              >
                Export bundle (.zip)
              </a>
            </p>
          </header>

          {floodCategories.length > 0 && (
            <div
              className="card"
              data-testid="flood-warning"
              style={{ borderColor: "var(--danger)" }}
            >
              <p className="eyebrow" style={{ color: "var(--danger)" }}>
                Precision warning — flagged as a possible flood
              </p>
              <p>
                {Object.entries(flood).map(([category, data]) => (
                  <span key={category} data-testid="flood-category">
                    {category}: {(data.rate * 100).toFixed(2)}% of examined windows, against a{" "}
                    {(data.ceiling * 100).toFixed(2)}% ceiling.{" "}
                  </span>
                ))}
                Nothing below has been removed — every claim is still present and
                inspectable. Model choice is the operator's lever.
              </p>
            </div>
          )}

          <section className="coverage-section" data-testid="inference-coverage">
            <p className="eyebrow">Coverage — how much of the eligible corpus was examined</p>
            {coverageRows.map(({ ref, unit }) => (
              <p key={ref.u} data-testid="coverage-row" className="coverage-row">
                {unit.text}
              </p>
            ))}
            {coverageRows.length === 0 && (
              <p data-testid="coverage-missing" style={{ color: "var(--danger)" }}>
                No coverage statement travelled with this version — it should never have
                been possible to finalize.
              </p>
            )}
            {notAskedRows.map(({ ref, unit }) => (
              <p key={ref.u} data-testid="inference-not-asked" className="dossier-row not-asked">
                not asked — {unit.text}
              </p>
            ))}
            {gapRows.map(({ ref }) => (
              <p key={ref.u} data-testid="inference-gap" className="dossier-row gap">
                searched, absent — examined windows produced nothing here.
              </p>
            ))}
          </section>

          <section className="inference-mention-note">
            <p style={{ color: "var(--ink-faint)" }} data-testid="mention-only-note">
              This run included windows where the subject only appears, not only where
              they spoke (mention-only is included by default; participant-only is an
              explicit opt-in set when the run was started).
            </p>
          </section>

          <section data-testid="inference-claims">
            {claimsAndRelations.map(({ ref, unit }) => (
              <ClaimRow key={ref.u} unit={unit} />
            ))}
            {claimsAndRelations.length === 0 && (
              <p data-testid="inference-empty" style={{ color: "var(--ink-faint)" }}>
                No {layout.ask === "soft_claims" ? "claims" : "relations"} survived
                grounding in this run.
              </p>
            )}
          </section>
        </main>
      </section>
      <EvidencePanel datasetId={datasetId} />
    </div>
  );
}

function ClaimRow({ unit }: { unit: Unit }) {
  const { select, selected } = useEvidence();
  const category = (unit.value as unknown as { category?: string } | null)?.category;
  const supportMethod = (unit.value as unknown as { support_method?: string } | null)
    ?.support_method;

  return (
    <div
      className={`dossier-row ${unit.kind}`}
      data-testid="claim-row"
      data-kind={unit.kind}
      data-category={category ?? ""}
    >
      <span data-testid="claim-row-text">{unit.text}</span>
      {category && (
        <span className="eyebrow" data-testid="claim-category">
          {category}
        </span>
      )}
      {supportMethod && (
        <span className="mono" data-testid="claim-support-method" title="How this claim was authored">
          {supportMethod}
        </span>
      )}
      <span className="cites" data-testid="claim-cites">
        {unit.citations.map((citation, index) => {
          const isSelected =
            selected?.blockId === citation.block_id &&
            selected.spans[0]?.start_cp === citation.start_cp;
          return (
            <button
              key={citation.span_id}
              className="cite"
              data-testid="claim-cite"
              aria-current={isSelected}
              title={`${citation.quote}${
                citation.support_method ? ` · grounded via ${citation.support_method}` : ""
              }`}
              onClick={() =>
                select({
                  blockId: citation.block_id,
                  spans: [
                    {
                      start_cp: citation.start_cp,
                      end_cp: citation.end_cp,
                      quote: citation.quote,
                    },
                  ],
                  label: `${index + 1}`,
                  origin: "inference",
                })
              }
            >
              {index + 1}
              {citation.support_method && (
                <span className="cite-method" data-testid="cite-support-method">
                  {" "}
                  {citation.support_method}
                </span>
              )}
            </button>
          );
        })}
      </span>
    </div>
  );
}
